import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

function SearchBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const { t } = useTranslation();

  // Read initial value from URL
  const getSearchFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get("search") || "";
  };

  const [value, setValue] = useState(getSearchFromUrl);
  const [isFocused, setIsFocused] = useState(false);

  // Sync input value when navigating back/forward
  useEffect(() => {
    setValue(getSearchFromUrl());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const doSearch = (term) => {
    const trimmed = term.trim();
    if (!trimmed) {
      // If cleared, navigate to shop without search param
      navigate("/shop");
      return;
    }
    navigate(`/search?search=${encodeURIComponent(trimmed)}`);
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setValue(val);

    // Debounce: fire 400ms after the user stops typing
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      doSearch(val);
    }, 400);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      // Fire immediately on Enter, cancel pending debounce
      if (debounceRef.current) clearTimeout(debounceRef.current);
      doSearch(value);
    }
    if (e.key === "Escape") {
      setValue("");
      navigate("/shop");
      inputRef.current?.blur();
    }
  };

  const handleSearchClick = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    doSearch(value);
  };

  const handleClear = () => {
    setValue("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    navigate("/shop");
    inputRef.current?.focus();
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <div className="w-full transition-all duration-200 ease-in-out lg:flex lg:max-w-[520px] xl:max-w-[750px] 2xl:max-w-[900px] md:mx-12 lg:mx-4 xl:mx-0">
      <div className="w-full flex flex-col justify-center flex-shrink-0 relative z-30">
        <div className="flex flex-col mx-auto w-full">
          <div
            className={`relative pr-12 md:pr-14 bg-white overflow-hidden shadow-sm rounded-md w-full transition-shadow duration-200 ${
              isFocused ? "ring-2 ring-emerald-400 shadow-md" : ""
            }`}
          >
            <label className="flex items-center py-0.5">
              <input
                ref={inputRef}
                value={value}
                name="search"
                autoComplete="off"
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                className="form-input w-full pl-5 appearance-none transition ease-in-out border text-input text-sm font-sans rounded-md min-h-10 h-10 duration-200 bg-white focus:ring-0 outline-none border-none focus:outline-none placeholder-gray-500 placeholder-opacity-75"
                placeholder={t('nav.searchPlaceholder')}
              />
            </label>

            {/* Clear button — only shows when there's text */}
            {value.length > 0 && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute top-0 right-12 md:right-14 h-full px-2 flex items-center text-gray-400 hover:text-gray-600 transition"
                aria-label="Clear search"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}

            {/* Search button */}
            <button
              type="button"
              onClick={handleSearchClick}
              aria-label="Search"
              className="outline-none text-xl text-gray-400 absolute top-0 right-0 end-0 w-12 md:w-14 h-full flex items-center justify-center transition duration-200 ease-in-out hover:text-emerald-500 focus:outline-none"
            >
              <svg
                stroke="currentColor"
                fill="currentColor"
                strokeWidth="0"
                viewBox="0 0 512 512"
                height="1em"
                width="1em"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill="none"
                  strokeMiterlimit="10"
                  strokeWidth="32"
                  d="M221.09 64a157.09 157.09 0 10157.09 157.09A157.1 157.1 0 00221.09 64z"
                />
                <path
                  fill="none"
                  strokeLinecap="round"
                  strokeMiterlimit="10"
                  strokeWidth="32"
                  d="M338.29 338.29L448 448"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SearchBar;
